import { AlertCircle, AlertTriangle } from "lucide-react";

import { cn } from "~/lib/cn";

type FieldError = {
  field: string;
  message: string;
};

type ValidationMessageProps = {
  /** "warning" for user/validation errors (4xx), "error" for server errors (5xx) */
  variant?: "warning" | "error";
  /** Title shown at the top */
  title?: string;
  /** General message or description */
  message?: string;
  /** List of field-specific errors */
  fieldErrors?: FieldError[];
  /** Optional mapping of field keys to human-readable labels */
  fieldLabels?: Partial<Record<string, string>>;
  /** Additional class names */
  className?: string;
};

const formatFieldLabel = (field: string): string =>
  field.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

/** Warning or error box that lists the fields that failed validation. */
export function ValidationMessage({
  variant = "warning",
  title,
  message,
  fieldErrors,
  fieldLabels = {},
  className,
}: ValidationMessageProps) {
  const isWarning = variant === "warning";

  const Icon = isWarning ? AlertTriangle : AlertCircle;

  const defaultTitle = isWarning
    ? "Please check your input"
    : "Something went wrong";

  return (
    <div
      className={cn(
        "rounded-lg border px-4 py-3 shadow-md",
        isWarning
          ? "border-amber-200 bg-amber-50 text-amber-900 shadow-amber-900/10"
          : "border-red-200 bg-red-50 text-red-900 shadow-red-900/10",
        className,
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            "mt-0.5 h-5 w-5 shrink-0",
            isWarning ? "text-amber-500" : "text-red-500",
          )}
        />
        <div className="min-w-0 flex-1">
          <h3
            className={cn(
              "font-medium",
              isWarning ? "text-amber-800" : "text-red-800",
            )}
          >
            {title ?? defaultTitle}
          </h3>

          {message && (
            <p
              className={cn(
                "mt-1 text-sm",
                isWarning ? "text-amber-700" : "text-red-700",
              )}
            >
              {message}
            </p>
          )}

          {fieldErrors && fieldErrors.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {fieldErrors.map(({ field, message: msg }, index) => (
                <li
                  key={`${field}-${index}`}
                  className={cn(
                    "flex gap-1.5",
                    isWarning ? "text-amber-700" : "text-red-700",
                  )}
                >
                  <span
                    className={cn(
                      "font-medium",
                      isWarning ? "text-amber-800" : "text-red-800",
                    )}
                  >
                    {fieldLabels[field] ?? formatFieldLabel(field)}:
                  </span>
                  <span>{msg}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Utility types for error parsing
export type ParsedValidationError = {
  type: "validation";
  fieldErrors: FieldError[];
};

export type ParsedServerError = {
  type: "server";
  message: string;
};

export type ParsedError = ParsedValidationError | ParsedServerError;

/**
 * Extract Zod issues from a potential issues array.
 */
function extractFieldErrors(
  issues: unknown,
): Array<{ field: string; message: string }> | null {
  if (!Array.isArray(issues)) return null;

  const fieldErrors = (issues as Array<{ path?: unknown[]; message?: string }>)
    .filter(
      (issue): issue is { path: unknown[]; message: string } =>
        Array.isArray(issue.path) && typeof issue.message === "string",
    )
    .map((issue) => ({
      field: String(issue.path[0] ?? "unknown"),
      message: issue.message,
    }));

  return fieldErrors.length > 0 ? fieldErrors : null;
}

/**
 * Parse errors from server functions (TanStack Start) or Zod validation.
 * Returns a structured error object that can be passed to ValidationMessage.
 */
export function parseServerError(error: unknown): ParsedError {
  // Check for error.cause (errors can be wrapped with cause)
  if (
    error &&
    typeof error === "object" &&
    "cause" in error &&
    error.cause &&
    typeof error.cause === "object"
  ) {
    const causeResult = parseServerError(error.cause);
    if (causeResult.type === "validation") {
      return causeResult;
    }
  }

  // Errors that carry validation issues on body.issues
  if (
    error &&
    typeof error === "object" &&
    "body" in error &&
    error.body &&
    typeof error.body === "object"
  ) {
    const body = error.body as Record<string, unknown>;
    const fieldErrors = extractFieldErrors(body.issues);
    if (fieldErrors) {
      return { type: "validation", fieldErrors };
    }
  }

  // TanStack Start wraps Zod errors - check for issues array in data
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object"
  ) {
    const data = error.data as Record<string, unknown>;
    const fieldErrors = extractFieldErrors(data.issues);
    if (fieldErrors) {
      return { type: "validation", fieldErrors };
    }
  }

  // Check for direct Zod error (issues at top level)
  if (error && typeof error === "object" && "issues" in error) {
    const fieldErrors = extractFieldErrors(
      (error as { issues: unknown }).issues,
    );
    if (fieldErrors) {
      return { type: "validation", fieldErrors };
    }
  }

  // Check for message property that might contain JSON-stringified Zod issues
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message: unknown }).message);

    // Try to parse the message as JSON (could be array or object with issues)
    if (message.startsWith("[") || message.startsWith("{")) {
      try {
        const parsed = JSON.parse(message) as unknown;

        // Handle direct array of issues
        const arrayFieldErrors = extractFieldErrors(parsed);
        if (arrayFieldErrors) {
          return { type: "validation", fieldErrors: arrayFieldErrors };
        }

        // Handle object with issues property (e.g., { issues: [...] })
        if (parsed && typeof parsed === "object" && "issues" in parsed) {
          const objectFieldErrors = extractFieldErrors(
            (parsed as { issues: unknown }).issues,
          );
          if (objectFieldErrors) {
            return { type: "validation", fieldErrors: objectFieldErrors };
          }
        }
      } catch {
        // Not valid JSON, treat as regular message
      }
    }

    return {
      type: "server",
      message,
    };
  }

  return {
    type: "server",
    message: "An unexpected error occurred",
  };
}

/**
 * Convert React Hook Form errors to field errors array.
 */
export function formErrorsToFieldErrors(
  errors: Record<string, { message?: string } | undefined>,
): FieldError[] {
  return Object.entries(errors)
    .filter(
      (entry): entry is [string, { message: string }] =>
        entry[1] !== undefined &&
        typeof entry[1].message === "string" &&
        entry[1].message.length > 0,
    )
    .map(([field, error]) => ({
      field,
      message: error.message,
    }));
}
